// Helpers for the Products grid save queue (App.js).
// Kept as pure functions so the queue behaviour is unit-testable.

import { cleanBarcodeForSave, isPlaceholderBarcode } from './barcodePlaceholders';
import { normalizeBarcodeList } from './productBarcodes';

let saveQueueIdSeq = 0;

/**
 * Collision-safe queue item id.
 * Date.now() alone collides when several items are queued in the same
 * millisecond (bulk "Save All Changes"), which made the queue mark and
 * remove multiple rows while only one request was actually sent.
 */
export function nextSaveQueueItemId() {
  saveQueueIdSeq += 1;
  return `save_${Date.now()}_${saveQueueIdSeq}`;
}

/**
 * Build the payload for POST /api/products/update-metafields-all-stores.
 * Editable values win; otherwise fall back to current product values so
 * the save propagates the full field set to other stores.
 */
export function buildProductSaveData(product, editable) {
  const saveData = {
    sku: product.sku,
    store: product.store
  };

  if (product.variantId) {
    saveData.variantId = product.variantId;
  }

  if (editable.newSku !== undefined) {
    saveData.newSku = editable.newSku;
  }

  if (editable.weight !== undefined) {
    saveData.weight = parseFloat(editable.weight);
  } else if (product.weight !== undefined) {
    saveData.weight = parseFloat(product.weight);
  }

  // The Products card's barcode list (APP-06): the variant's FULL typed set,
  // which replaces Shopify's. It wins over a single barcode, never both.
  if (Array.isArray(editable.barcodes)) {
    saveData.barcodes = normalizeBarcodeList(editable.barcodes);
    // The set the editor opened with: the server applies the edit onto
    // Shopify's live set as a diff, so barcodes added elsewhere survive.
    if (Array.isArray(editable.barcodesBase)) saveData.barcodesBase = normalizeBarcodeList(editable.barcodesBase);
  } else if (editable.barcode !== undefined) {
    saveData.barcode = cleanBarcodeForSave(editable.barcode);
  } else if (product.barcode !== undefined && !product.noBarcodeAck && !isPlaceholderBarcode(product.barcode)) {
    saveData.barcode = product.barcode;
  }

  if (editable.binLocation !== undefined) {
    saveData.binLocation = editable.binLocation;
  } else if (product.binLocation !== undefined) {
    saveData.binLocation = product.binLocation;
  }

  if (editable.overflow1 !== undefined) {
    saveData.overflow1 = editable.overflow1;
  } else if (product.overflow1 !== undefined) {
    saveData.overflow1 = product.overflow1;
  }

  if (editable.overflow2 !== undefined) {
    saveData.overflow2 = editable.overflow2;
  } else if (product.overflow2 !== undefined) {
    saveData.overflow2 = product.overflow2;
  }

  // Shipping dimensions (cm) - product-level metafields custom.length/width/height.
  // Empty string clears the metafield; fall back to product values so a save
  // propagates existing dimensions to the other stores.
  ['length', 'width', 'height'].forEach((dim) => {
    if (editable[dim] !== undefined) {
      saveData[dim] = editable[dim] === '' ? '' : parseFloat(editable[dim]);
    } else if (product[dim] !== undefined && product[dim] !== null) {
      saveData[dim] = parseFloat(product[dim]);
    }
  });

  return saveData;
}

/**
 * Summarise per-store results from update-metafields-all-stores.
 * A store counts as successful only when success === true with no errors.
 */
export function summarizeStoreResults(responseResults) {
  const results = Array.isArray(responseResults) ? responseResults : [];
  const failedResults = results.filter(r =>
    r.success === false || (Array.isArray(r.errors) && r.errors.length > 0)
  );
  const successfulStores = results.filter(r =>
    r.success === true && (!Array.isArray(r.errors) || r.errors.length === 0)
  ).length;
  return { failedResults, successfulStores };
}

/**
 * After a confirmed save, clear only the fields that were saved and only
 * when the current editable value still matches what was queued. Edits the
 * user made while the save was in flight are kept.
 *
 * Returns the new editable object for the key, or null when nothing remains.
 */
export function clearSavedFieldsFromEditable(currentEditable, savedEditable) {
  if (!currentEditable) return null;
  const updated = { ...currentEditable };
  Object.keys(savedEditable || {}).forEach(field => {
    if (updated[field] === savedEditable[field]) {
      delete updated[field];
    }
  });
  return Object.keys(updated).length === 0 ? null : updated;
}
